import React, { useRef } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import "./RoomForm.css";

// Camera and library pickers over a grid of thumbs. Every part of the check-in
// that collects photos — a room, its damage close-ups, the keys — shows the
// same block; only the labels and where the files are posted differ.
// Once the check-in is signed off the photos are the record: the grid stays,
// the pickers and the delete crosses go.
const PhotoSection = ({
  title,
  cameraLabel,
  photos,
  pendingCount,
  alt,
  deleteLabel,
  isUploading,
  onPick,
  onDelete,
  deletingPhotoId,
  isDeletingPhoto,
  isLocked = false,
  errors = [],
}) => {
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  const pickPhotos = (event) => {
    onPick(event.target.files);
    // Let the same file be picked again after an upload or a cancel.
    event.target.value = "";
  };

  return (
    <>
      <div className="rf-photos-head">
        <h3 className="cf-section-title">{title}</h3>
        <span className="rf-photos-count">
          {pendingCount > 0
            ? `Uploading ${pendingCount}…`
            : `${photos.length} stored`}
        </span>
      </div>

      {!isLocked && (
        <>
          <div className="rf-choices">
            <button
              type="button"
              className="rf-photo-btn"
              disabled={isUploading}
              onClick={() => cameraInputRef.current?.click()}
            >
              <PhotoCameraOutlinedIcon />
              {cameraLabel}
            </button>
            <button
              type="button"
              className="rf-photo-btn"
              disabled={isUploading}
              onClick={() => libraryInputRef.current?.click()}
            >
              <CollectionsOutlinedIcon />
              Library
            </button>
          </div>

          <input
            ref={cameraInputRef}
            className="rf-file-input"
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={pickPhotos}
          />
          <input
            ref={libraryInputRef}
            className="rf-file-input"
            type="file"
            accept="image/*"
            multiple
            onChange={pickPhotos}
          />
        </>
      )}

      {isLocked && photos.length === 0 && (
        <p className="rf-help">No photos were added.</p>
      )}

      {(photos.length > 0 || pendingCount > 0) && (
        <ul className="rf-thumbs">
          {photos.map((photo) => (
            <li key={photo.id} className="rf-thumb">
              <img src={photo.photo_blob_url} alt={photo.comments || alt} />
              <span className="rf-thumb-stored" aria-label="Uploaded">
                <CheckIcon />
              </span>
              {!isLocked && (
                <button
                  type="button"
                  className="rf-thumb-delete"
                  aria-label={deleteLabel}
                  disabled={isDeletingPhoto}
                  onClick={() => onDelete(photo.id)}
                >
                  {deletingPhotoId === photo.id ? (
                    <span className="cf-spinner rf-thumb-spinner" />
                  ) : (
                    <CloseIcon />
                  )}
                </button>
              )}
            </li>
          ))}

          {Array.from({ length: pendingCount }, (_, index) => (
            <li
              key={`pending-${index}`}
              className="rf-thumb rf-thumb--pending"
              aria-label="Uploading photo"
            />
          ))}
        </ul>
      )}

      {errors.map((message, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={index} className="rf-error" role="alert">
          {message}
        </p>
      ))}
    </>
  );
};

export default PhotoSection;
